import React, { useEffect, useState, useRef } from "react";
// Helper to format date as 'Month Day, Year at h:mm AM/PM'
function formatLongDate(dateInput) {
  if (!dateInput) return '';
  let dateObj;
  if (typeof dateInput === 'object' && dateInput.seconds) {
    dateObj = new Date(dateInput.seconds * 1000);
  } else {
    dateObj = new Date(dateInput);
  }
  if (isNaN(dateObj)) return '';
  const day = dateObj.getDate();
  const daySuffix = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  const month = dateObj.toLocaleString(undefined, { month: 'long' });
  const year = dateObj.getFullYear();
  const timeStr = dateObj.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${month} ${day}${daySuffix(day)}, ${year} at ${timeStr}`;
}
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../utils/firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
  query as fsQuery,
  where,
  serverTimestamp
} from "firebase/firestore";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Link,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Alert,
  Switch,
  FormControlLabel
} from "@mui/material";


import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from "../context/AuthContext";
import EventSignupQuestions from "../components/EventSignupQuestions";
import SignupAnswersSection from "../components/SignupAnswersSection";

function getAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth)) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function groupByGender(participants) {
  const grouped = { male: [], female: [] };
  participants.forEach((p) => {
    const gender = (p.gender || "").toString().toLowerCase().trim();
    if (gender === "male") grouped.male.push(p);
    else if (gender === "female") grouped.female.push(p);
  });
  return grouped;
}

export default function EventDetailsPage() {
  // ...existing code...

  // Move these inside the component so they have access to state/props
  const handleModeChange = async (newMode) => {
    setMode(newMode);
    await updateDoc(doc(db, "events", eventId), { mode: newMode });
    setEvent(ev => ({ ...ev, mode: newMode }));
  };
  const handleSignupLimitChange = async (newLimit) => {
    setSignupLimit(newLimit);
    await updateDoc(doc(db, "events", eventId), { signupLimit: newLimit });
    setEvent(ev => ({ ...ev, signupLimit: newLimit }));
  };
  const handleVisibilityToggle = async (newVisible) => {
    if (!event) return;
    try {
      await updateDoc(doc(db, "events", eventId), { visible: newVisible });
      setEvent(ev => ({ ...ev, visible: newVisible }));
    } catch (err) {
      alert("Failed to update visibility: " + err.message);
    }
  };
  const [event, setEvent] = useState(null);
  const [mode, setMode] = useState("");
  const [signupLimit, setSignupLimit] = useState("");
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState({ male: [], female: [] });
  const [allParticipantsRaw, setAllParticipantsRaw] = useState([]); // raw participant docs for this event
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedSignupAnswers, setSelectedSignupAnswers] = useState(null);
  const [participantModalOpen, setParticipantModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState({ name: '', date: '', location: '', summary: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  // Admin matches matrix state
  const [matchesMatrix, setMatchesMatrix] = useState(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState('');
  useEffect(() => {
    if (event) {
      setMode(event.mode || "signups");
      setSignupLimit(event.signupLimit || "");
    }
  }, [event]);
  // For debouncing note saves
  const noteSaveTimeouts = useRef({});
  const { user, role } = useAuth();
  const { eventId } = useParams();
  const navigate = useNavigate();
  // Derive myPart (current user's participant record for this event) from loaded participants
  const myPart = React.useMemo(() => {
    if (!user || !allParticipantsRaw.length) return null;
    return allParticipantsRaw.find(p => (p.userId || p.uid) === user.uid) || null;
  }, [user, allParticipantsRaw]);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const evSnap = await getDoc(doc(db, "events", eventId));
        if (!evSnap.exists()) {
          setEvent(null);
          setLoading(false);
          return;
        }
        const evData = { id: evSnap.id, ...evSnap.data() };
        setEvent(evData);
      } catch (e) {
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  // Fetch participants for this event from top-level participants collection.
  useEffect(() => {
    if (!eventId) return;
    const loadParticipants = async () => {
      try {
        const q = fsQuery(collection(db, "participants"), where("eventId", "==", eventId));
        const snap = await getDocs(q);
        let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // If some rows have userId but missing name/email/gender, batch-fetch user docs.
        const needUserFetch = rows.filter(r => r.userId && (!r.name || !r.gender || !r.email));
        if (needUserFetch.length) {
          const uniqueUserIds = [...new Set(needUserFetch.map(r => r.userId))];
            const userSnaps = await Promise.all(uniqueUserIds.map(uid => getDoc(doc(db, "users", uid))));
            const userById = new Map(userSnaps.filter(s => s.exists()).map(s => [s.id, s.data()]));
            rows = rows.map(r => {
              if (r.userId && userById.has(r.userId)) {
                const u = userById.get(r.userId);
                return {
                  ...u,
                  ...r,
                  // ensure priority to participant-specific overrides (status etc.)
                  uid: r.userId,
                  name: r.name || u.name || u.email,
                  email: r.email || u.email,
                  gender: (r.gender || u.gender || '').toLowerCase(),
                  dob: r.dob || u.dob,
                };
              }
              return { ...r, uid: r.userId || r.id };
            });
        } else {
          rows = rows.map(r => ({ ...r, uid: r.userId || r.id }));
        }
        setAllParticipantsRaw(rows);
        setParticipants(groupByGender(rows));
      } catch (e) {
        // swallow for now
      }
    };
    loadParticipants();
  }, [eventId]);

  // Admin: fetch matches matrix for this event
  useEffect(() => {
    (async () => {
      if (role !== 'admin' || !eventId || !user) return;
      setMatchesLoading(true);
      setMatchesError('');
      try {
        const token = await user.getIdToken();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
  const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/admin/matrix`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.message || json?.meta?.reason || `HTTP ${resp.status}`);
        setMatchesMatrix(json);
      } catch (e) {
        setMatchesError(e.message || 'Failed to load matches matrix');
        setMatchesMatrix(null);
      } finally {
        setMatchesLoading(false);
      }
    })();
  }, [role, eventId, user]);

  // Save admin note for a participant (debounced Firestore update)
  const handleNoteChange = (participantUid, note) => {
    // Optimistic update: update selectedParticipant and participants lists immediately
    if (selectedParticipant && selectedParticipant.uid === participantUid) {
      setSelectedParticipant(sp => ({ ...sp, note }));
    }
    setAllParticipantsRaw(prev => prev.map(p => (p.uid || p.userId || p.id) === participantUid ? { ...p, note } : p));
    setParticipants(prev => {
      const merged = [...prev.male, ...prev.female].map(p => (p.uid || p.userId || p.id) === participantUid ? { ...p, note } : p);
      return groupByGender(merged);
    });
    // Debounce Firestore write
    if (noteSaveTimeouts.current[participantUid]) clearTimeout(noteSaveTimeouts.current[participantUid]);
    const scheduledAt = Date.now();
    noteSaveTimeouts.current[participantUid] = setTimeout(async () => {
      const part = allParticipantsRaw.find(p => (p.uid || p.userId || p.id) === participantUid);
      if (!part) return;
      try {
        await updateDoc(doc(db, 'participants', part.id), { note });
      } catch (e) {
        // silent
      }
    }, 450);
  };

  // Cleanup pending note save timers on unmount
  useEffect(() => {
    return () => {
      Object.values(noteSaveTimeouts.current).forEach(t => clearTimeout(t));
    };
  }, []);

  // Remove participant from event entirely if fullRemove is true, else set status to 'cancelled'
  const handleRemoveParticipant = async (participant, fullRemove = false) => {
    if (!participant) return;
    try {
      if (fullRemove) {
        await deleteDoc(doc(db, "participants", participant.id));
      } else {
        await updateDoc(doc(db, "participants", participant.id), { status: 'cancelled' });
      }
    } catch (e) {
      // ignore
    }
    // refetch
    const q = fsQuery(collection(db, "participants"), where("eventId", "==", eventId));
    const snap = await getDocs(q);
    let rows = snap.docs.map(d => ({ id: d.id, ...d.data(), uid: d.data().userId || d.id }));
    // Enrich rows missing display fields
    const needUserFetch = rows.filter(r => r.userId && (!r.name || !r.email || !r.gender));
    if (needUserFetch.length) {
      try {
        const uniqueUserIds = [...new Set(needUserFetch.map(r => r.userId))];
        const userSnaps = await Promise.all(uniqueUserIds.map(uid => getDoc(doc(db, 'users', uid))));
        const userById = new Map(userSnaps.filter(s => s.exists()).map(s => [s.id, s.data()]));
        rows = rows.map(r => {
          if (r.userId && userById.has(r.userId)) {
            const u = userById.get(r.userId);
            return {
              ...r,
              name: r.name || u.name || u.email,
              email: r.email || u.email,
              gender: (r.gender || u.gender || '').toLowerCase(),
              dob: r.dob || u.dob
            };
          }
          return r;
        });
      } catch(e) {
        // swallow enrichment errors
      }
    }
    setAllParticipantsRaw(rows);
    setParticipants(groupByGender(rows));
  };

  const handleChangeStatus = async (participant, newStatus) => {
    if (!participant) return;
    try {
      await updateDoc(doc(db, "participants", participant.id), { status: newStatus });
    } catch (e) {
      // ignore
    }
    // optimistic update
    setAllParticipantsRaw(prev => prev.map(p => p.id === participant.id ? { ...p, status: newStatus } : p));
    setParticipants(prev => {
      const merged = [...prev.male, ...prev.female].map(p => p.id === participant.id ? { ...p, status: newStatus } : p);
      return groupByGender(merged);
    });
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearch(value);
    if (!value) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const usersCol = collection(db, "users");
    const userDocs = await getDocs(usersCol);
  // Get current participant userIds from participants collection results
  const currentUids = new Set(allParticipantsRaw.map(p => p.userId || p.uid));
    const results = userDocs.docs
      .map(doc => ({ uid: doc.id, ...doc.data() }))
      .filter(u => (
        ((u.name && u.name.toLowerCase().includes(value.toLowerCase())) || (u.email && u.email.toLowerCase().includes(value.toLowerCase())))
        && !currentUids.has(u.uid)
      ));
    setSearchResults(results);
    setSearchLoading(false);
  };

  const handleAddParticipant = async (userObj) => {
    if (!userObj || !eventId) return;
    // prevent duplicates (check current participants in memory)
    const existing = allParticipantsRaw.some(p => (p.userId || p.uid) === userObj.uid);
    if (existing) return;
    // Build payload ensuring no undefined values
    const payload = {
      eventId: String(eventId),
      userId: userObj.uid,
      status: 'pending',
      createdAt: serverTimestamp()
    };
    if (userObj.name) payload.name = userObj.name;
    if (userObj.email) payload.email = userObj.email;
    if (userObj.gender) payload.gender = String(userObj.gender).toLowerCase();
    if (userObj.dob) payload.dob = userObj.dob; // assume ISO or YYYY-MM-DD string
    try {
      const newDoc = await addDoc(collection(db, "participants"), payload);
      const newPart = { id: newDoc.id, uid: userObj.uid, userId: userObj.uid, ...payload, createdAt: new Date() };
      setAllParticipantsRaw(prev => [...prev, newPart]);
      setParticipants(prev => groupByGender([...prev.male, ...prev.female, newPart]));
      setSearchResults(prev => prev.filter(u => u.uid !== userObj.uid));
    } catch (e) {
      // silent
    }
  };

  if (loading) return <CircularProgress />;
  if (!event) return <Typography>Event not found.</Typography>;

  // Open edit dialog and prefill values
  const openEditDialog = () => {
    if (!event) return;
    setEditValues({
      name: event.name || '',
      date: event.date && event.date.seconds ? new Date(event.date.seconds * 1000).toISOString().slice(0, 16) : (event.date ? new Date(event.date).toISOString().slice(0, 16) : ''),
      generalLocation: event.generalLocation || '',
      specificLocation: event.specificLocation || '',
      summary: event.summary || event.description || '',
    });
    setEditOpen(true);
  };

  // Save event edits
  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      let imageUrl = event.imageUrl;
      if (editValues.image) {
        try {
          const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
          const storageRef = ref(
            (await import("../utils/firebase")).storage,
            `eventImages/${Date.now()}_${editValues.image.name}`
          );
          await uploadBytes(storageRef, editValues.image);
          imageUrl = await getDownloadURL(storageRef);
        } catch (e) {
          alert("Failed to upload image.");
          setEditSaving(false);
          return;
        }
      }
      const eventDocRef = doc(db, "events", eventId);
      const updateData = {
        name: editValues.name,
        generalLocation: editValues.generalLocation,
        specificLocation: editValues.specificLocation,
        summary: editValues.summary,
        // Store as Firestore Timestamp if possible, else ISO string
        date: editValues.date ? new Date(editValues.date) : '',
        imageUrl,
      };
      await updateDoc(eventDocRef, updateData);
      setEvent(prev => ({ ...prev, ...updateData }));
      setEditOpen(false);
    } catch (e) {
      alert("Failed to update event.");
    }
    setEditSaving(false);
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", mt: { xs: 2, sm: 4 }, p: { xs: 1, sm: 3 }, bgcolor: '#fafbfc', borderRadius: 3, boxShadow: 2 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2, width: { xs: '100%', sm: 'auto' } }} variant="outlined">Back to Events</Button>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', mb: 2, gap: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {event.imageUrl && (
            <Tooltip title="Click to preview" placement="right">
              <Box
                component="img"
                src={event.imageUrl}
                alt="Event Thumbnail"
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  boxShadow: 1,
                  cursor: 'pointer'
                }}
                onClick={() => setImagePreviewOpen(true)}
              />
            </Tooltip>
          )}
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: 20, sm: 26 }, m: 0 }}>
            {event.name}
          </Typography>
        </Box>
        {role === 'admin' && (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button variant="contained" color="primary" onClick={openEditDialog} sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>Edit Event</Button>
            <Button variant="outlined" color="primary" onClick={() => navigate(`/admin/event/${eventId}/participants`)} sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>
              View Matches
            </Button>
          </Box>
        )}
      </Box>
      {/* ADMIN EVENT MODE CONTROLS */}
      {role === 'admin' && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Event Mode</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Mode</InputLabel>
              <Select value={mode} label="Mode" onChange={e => handleModeChange(e.target.value)}>
                <MenuItem value="signups">Sign-ups</MenuItem>
                <MenuItem value="limited-signups">Limited Sign-ups</MenuItem>
                <MenuItem value="in-progress">Event In Progress</MenuItem>
                <MenuItem value="matching">Matching</MenuItem>
                <MenuItem value="finished">Finished</MenuItem>
              </Select>
            </FormControl>
            {mode === 'limited-signups' && (
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Signup Limit</InputLabel>
                <Select value={signupLimit} label="Signup Limit" onChange={e => handleSignupLimitChange(e.target.value)}>
                  <MenuItem value="male">Male Only</MenuItem>
                  <MenuItem value="female">Female Only</MenuItem>
                  <MenuItem value="none">No Sign-ups</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={{ my: 2 }}>
            <hr style={{ border: 0, borderTop: '1px solid #e0e0e0', margin: '16px 0' }} />
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Visibility</InputLabel>
              <Select
                value={event.visible !== false ? "visible" : "hidden"}
                label="Visibility"
                onChange={e => handleVisibilityToggle(e.target.value === "visible")}
              >
                <MenuItem value="visible">Visible to users</MenuItem>
                <MenuItem value="hidden">Hidden from users</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}
      
      <Dialog open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} maxWidth="md">
        <DialogTitle>Event Image</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {event?.imageUrl && (
            <Box sx={{ maxWidth: '80vw', maxHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <Box
                component="img"
                src={event.imageUrl}
                alt="Event"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Event Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Event Name"
            value={editValues.name}
            onChange={e => setEditValues(v => ({ ...v, name: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Date & Time"
            type="datetime-local"
            value={editValues.date}
            onChange={e => setEditValues(v => ({ ...v, date: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="General Location (public)"
            value={editValues.generalLocation || ''}
            onChange={e => setEditValues(v => ({ ...v, generalLocation: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Specific Location (hidden until paid)"
            value={editValues.specificLocation || ''}
            onChange={e => setEditValues(v => ({ ...v, specificLocation: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Summary / Description"
            value={editValues.summary}
            onChange={e => setEditValues(v => ({ ...v, summary: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <Button variant="outlined" component="label" sx={{ mt: 1 }}>
            {editValues.image ? (editValues.image.name || "Change Event Image") : "Change Event Image"}
            <input type="file" hidden accept="image/*" onChange={e => setEditValues(v => ({ ...v, image: e.target.files[0] }))} />
          </Button>

          {/* Danger Zone: Archive / Delete inside edit dialog */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fffbe7', border: '1px solid #ffe082', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Danger Zone</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color={event?.archived ? 'warning' : 'secondary'}
                onClick={() => { setEditOpen(false); setArchiveConfirm(true); }}
              >
                {event?.archived ? 'Unarchive Event' : 'Archive Event'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={!event?.archived}
                onClick={() => { setEditOpen(false); setDeleteConfirm(true); }}
              >
                Delete Event
              </Button>
            </Box>
            {!event?.archived && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#a66' }}>
                You must archive the event before it can be deleted.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={editSaving}>
            {editSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      
      {/* Archive Event Confirmation */}
      <Dialog open={archiveConfirm} onClose={() => setArchiveConfirm(false)}>
        <DialogTitle>{event.archived ? "Unarchive Event" : "Archive Event"}</DialogTitle>
        <DialogContent>
          <Typography>
            {event.archived
              ? "This will unarchive the event and make it active again."
              : "Archiving will hide this event from users but retain all data. You can unarchive at any time."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveConfirm(false)} disabled={archiveLoading}>Cancel</Button>
      <Button color="warning" variant="contained" onClick={async () => {
            setArchiveLoading(true);
            try {
        const archiving = !event.archived;
        const payload = archiving ? { archived: true, visible: false } : { archived: false };
        await updateDoc(doc(db, "events", eventId), payload);
        setEvent(prev => ({ ...prev, ...payload }));
              setArchiveConfirm(false);
            } catch (e) {
              alert("Failed to update archive status.");
            }
            setArchiveLoading(false);
          }} disabled={archiveLoading}>
            {archiveLoading ? (event.archived ? "Unarchiving..." : "Archiving...") : (event.archived ? "Unarchive" : "Archive")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Event Confirmation - only if archived */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography color="error" sx={{ mb: 2 }}>
            This will permanently delete the event, all participation, all matches, and any match severances. This cannot be undone.
          </Typography>
          {!event.archived && (
            <Typography color="warning.main" sx={{ mb: 2 }}>
              You must archive the event before it can be deleted.
            </Typography>
          )}
          <Typography sx={{ mb: 1 }}>To confirm, type <b>DELETE EVENT</b> below:</Typography>
          <Input
            fullWidth
            value={deleteInput}
            onChange={e => setDeleteInput(e.target.value)}
            disabled={!event.archived || deleting}
            placeholder="DELETE EVENT"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained"
            disabled={!event.archived || deleteInput !== "DELETE EVENT" || deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                if (!user) throw new Error('Not authenticated');
                const token = await user.getIdToken();
                const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
                const resp = await fetch(`${backendUrl}/api/event/${eventId}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${token}` }
                });
                const json = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(json?.message || `HTTP ${resp.status}`);
                setDeleteConfirm(false);
                navigate("/admin");
              } catch (e) {
                setDeleting(false);
                alert(e.message || 'Failed to delete event.');
              }
            }}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 }, mb: 2 }}>
        <Box sx={{ flex: 1, mb: { xs: 2, md: 0 } }}>
          <Typography variant="subtitle1" gutterBottom><b>Date:</b> {formatLongDate(event.date)}</Typography>
          {/* Admin view: list general and specific separately; map link lives beside specific location */}
          <Typography variant="subtitle1" gutterBottom>
            <b>General Location:</b> {event.generalLocation || 'TBA'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <b>Specific Location:</b> {
              role === 'admin'
                ? (event.specificLocation || 'TBA')
                : (myPart && myPart.status === 'approved_paid' ? event.specificLocation : 'Hidden until payment')
            }
            {role === 'admin' && !!event.specificLocation && (
              <IconButton
                size="small"
                sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}
                component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.specificLocation)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in Google Maps"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="#4285F4" style={{ marginRight: 4 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                <span style={{ fontWeight: 500, color: '#4285F4', fontSize: 15 }}>Map it</span>
              </IconButton>
            )}
          </Typography>
        </Box>
  <Box sx={{ flex: 2 }}>
          <Typography variant="subtitle1" gutterBottom><b>Summary:</b> {event.summary || event.description || "No summary provided."}</Typography>
        </Box>
      </Box>
      <Box sx={{ mt: 4, mb: 2, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: { xs: 20, sm: 24 } }}>Participants</Typography>
        {role === 'admin' && (() => {
          const counts = { pending: { male: 0, female: 0 }, approved_pending_payment: { male: 0, female: 0 }, approved_paid: { male: 0, female: 0 }, cancelled: { male: 0, female: 0 }, total: { male: 0, female: 0 } };
          allParticipantsRaw.forEach(p => {
            const g = (p.gender || '').toLowerCase();
            if (g !== 'male' && g !== 'female') return;
            const st = p.status || 'pending';
            counts.total[g]++;
            if (counts[st]) counts[st][g]++;
          });
          const Cell = ({ label, male, female, highlight }) => (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1,
              bgcolor: highlight ? '#eef7ff' : '#fafafa',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              fontSize: 13,
              fontFamily: (theme) => theme.typography.fontFamily,
              lineHeight: 1.4
            }}>
              <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box>
              <Box component="span" sx={{ fontFamily: 'inherit', letterSpacing: 0.25 }}>
                M: {male} | F: {female} | Total: {male + female}
              </Box>
            </Box>
          );
          return (
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: 15, sm: 16 } }}>Status Breakdown</Typography>
              <Cell label="Pending" male={counts.pending.male} female={counts.pending.female} />
              <Cell label="Approved - Pending Payment" male={counts.approved_pending_payment.male} female={counts.approved_pending_payment.female} />
              <Cell label="Approved & Paid" male={counts.approved_paid.male} female={counts.approved_paid.female} highlight />
              <Cell label="Cancelled" male={counts.cancelled.male} female={counts.cancelled.female} />
              <Cell label="Total" male={counts.total.male} female={counts.total.female} />
            </Box>
          );
        })()}
        <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 2, sm: 0 } }}>
            <Typography variant="h6" sx={{ color: '#d81b60', fontWeight: 600, mb: 1 }}>Women ({participants.female.length})</Typography>
            {participants.female.length === 0 && <Typography>No women participants.</Typography>}
            <Box>
              {participants.female.map((p, i) => {
                const age = getAge(p.dob);
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, p: 1, borderRadius: 1, bgcolor: '#fce4ec', boxShadow: 0, border: '1px solid #f8bbd0', cursor: 'pointer' }}
                    onClick={async () => {
                      // answers now live directly on participant doc (field 'answers')
                      setSelectedParticipant({ ...p, id: p.id, note: p.note || '', signupAnswers: p.answers || p.signupAnswers || {} });
                      setSelectedSignupAnswers(p.answers || p.signupAnswers || {});
                      setParticipantModalOpen(true);
                    }}
                  >
                    <Tooltip title="View Profile">
                      <IconButton
                        href={`/admin/user/${p.uid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{ color: '#d81b60', mr: 1 }}
                        onClick={e => e.stopPropagation()}
                      >
                        <PersonIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                      {p.name || p.email || p.id || p.uid}{age ? ` (${age})` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ bgcolor: '#fff', color: '#d81b60', px: 1, borderRadius: 1, border: '1px solid #f8bbd0', fontWeight: 600, ml: 1 }}>
                      {p.status === 'pending' && 'Pending'}
                      {p.status === 'approved_pending_payment' && 'Approved - Pending Payment'}
                      {p.status === 'approved_paid' && 'Approved & Paid'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600, mb: 1 }}>Men ({participants.male.length})</Typography>
            {participants.male.length === 0 && <Typography>No men participants.</Typography>}
            <Box>
              {participants.male.map((p, i) => {
                const age = getAge(p.dob);
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, p: 1, borderRadius: 1, bgcolor: '#e3f2fd', boxShadow: 0, border: '1px solid #90caf9', cursor: 'pointer' }}
                    onClick={async () => {
                      setSelectedParticipant({ ...p, id: p.id, note: p.note || '', signupAnswers: p.answers || p.signupAnswers || {} });
                      setSelectedSignupAnswers(p.answers || p.signupAnswers || {});
                      setParticipantModalOpen(true);
                    }}
                  >
                    <Tooltip title="View Profile">
                      <IconButton
                        href={`/admin/user/${p.uid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{ color: '#1976d2', mr: 1 }}
                        onClick={e => e.stopPropagation()}
                      >
                        <PersonIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                      {p.name || p.email || p.id || p.uid}{age ? ` (${age})` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ bgcolor: '#fff', color: '#1976d2', px: 1, borderRadius: 1, border: '1px solid #90caf9', fontWeight: 600, ml: 1 }}>
                      {p.status === 'pending' && 'Pending'}
                      {p.status === 'approved_pending_payment' && 'Approved - Pending Payment'}
                      {p.status === 'approved_paid' && 'Approved & Paid'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

      {/* Participant Modal for admin controls */}
  <Dialog open={participantModalOpen} onClose={() => { setParticipantModalOpen(false); setSelectedSignupAnswers(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Participant Details</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {selectedParticipant && (
            <>
              <Typography variant="subtitle1" fontWeight={600}>{selectedParticipant.name || selectedParticipant.email || selectedParticipant.uid}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Status: {selectedParticipant.status === 'cancelled' ? 'Cancelled' :
                  selectedParticipant.status === 'pending' ? 'Pending' :
                  selectedParticipant.status === 'approved_pending_payment' ? 'Approved - Pending Payment' :
                  selectedParticipant.status === 'approved_paid' ? 'Approved & Paid' : selectedParticipant.status}
              </Typography>
              {role === 'admin' && (
                <FormControl size="small" sx={{ minWidth: 140, mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedParticipant.status || 'pending'}
                    label="Status"
                    onChange={async e => {
                      await handleChangeStatus(selectedParticipant, e.target.value);
                      setSelectedParticipant(sp => ({ ...sp, status: e.target.value }));
                    }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved_pending_payment">Approved - Pending Payment</MenuItem>
                    <MenuItem value="approved_paid">Approved & Paid</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              )}
              {/* Show signup answers, editable only if admin clicks Edit or if user is editing their own */}
              {(role === 'admin' || (user && selectedParticipant && user.uid === selectedParticipant.uid)) && (
                <SignupAnswersSection
                  participant={selectedParticipant}
                  signupAnswers={selectedSignupAnswers}
                  event={event}
                  eventId={eventId}
                  isAdmin={role === 'admin'}
                  isSelf={user && selectedParticipant && user.uid === selectedParticipant.uid}
                  setEvent={setEvent}
                  setSelectedParticipant={setSelectedParticipant}
                />
              )}
              {role === 'admin' && (
                <TextField
                  label="Admin Notes"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mt: 1 }}
                  value={selectedParticipant.note || ''}
                  onChange={e => handleNoteChange(selectedParticipant.uid, e.target.value)}
                  InputProps={{ style: { background: '#fffbe7' } }}
                />
              )}
              {/* Event signup questions - admin and user view */}
              {(role === 'admin' || (user && selectedParticipant && user.uid === selectedParticipant.uid)) && selectedParticipant.eventSignupAnswers && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Event Signup Answers</Typography>
                  <EventSignupQuestions
                    answers={selectedParticipant.eventSignupAnswers}
                    editable={role === 'admin'}
                    onEdit={async (newAnswers) => {
                      if (role === 'admin' && selectedParticipant) {
                        // Admin editing answers
                        const eventDocRef = doc(db, "events", eventId);
                        const eventsCol = collection(db, "events");
                        const eventDocs = await getDocs(eventsCol);
                        const eventData = eventDocs.docs.find(doc => doc.id === eventId)?.data();
                        let updated = (eventData?.participants || []).map(p => {
                          if ((typeof p === 'string' ? p : p.uid) === selectedParticipant.uid) {
                            return { uid: selectedParticipant.uid, status: p.status, eventSignupAnswers: newAnswers };
                          }
                          return p;
                        });
                        await updateDoc(eventDocRef, { participants: updated });
                        setSelectedParticipant(sp => ({ ...sp, eventSignupAnswers: newAnswers }));
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {(role === 'admin' || (user && selectedParticipant && user.uid === selectedParticipant.uid)) && selectedParticipant && selectedParticipant.status !== 'cancelled' && (
            <Button color="error" onClick={() => setRemoveConfirmOpen(true)}>
              Cancel Participation
            </Button>
          )}
          {role === 'admin' && (
            <Button color="error" variant="outlined" onClick={() => setRemoveConfirmOpen('full')}>
              Remove from Event
            </Button>
          )}
          <Button onClick={() => setParticipantModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Remove Participant Confirmation Dialog */}
      <Dialog open={!!removeConfirmOpen} onClose={() => setRemoveConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography>
            {removeConfirmOpen === 'full'
              ? 'Are you sure you want to completely remove this participant from the event? This action cannot be undone.'
              : 'Are you sure you want to cancel this participant’s participation?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button color="error" onClick={async () => {
            await handleRemoveParticipant(selectedParticipant, removeConfirmOpen === 'full');
            setRemoveConfirmOpen(false);
            setParticipantModalOpen(false);
          }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
        {role === 'admin' && (
          <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, boxShadow: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Add Participant</Typography>
            <TextField
              placeholder="Search by name or email"
              size="small"
              fullWidth
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <SearchIcon position="start" />
                )
              }}
              sx={{ mb: 2 }}
            />
            {searchLoading && <Typography>Searching...</Typography>}
            {search && searchResults.length === 0 && !searchLoading && (
              <Typography>No users found.</Typography>
            )}
            {searchResults.length > 0 && (
              <Box>
                {searchResults.map(u => (
                  <Box key={u.uid} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {u.name} ({u.email})
                      {u.gender && (
                        <span style={{ marginLeft: 8, color: u.gender.toLowerCase() === 'male' ? '#1976d2' : u.gender.toLowerCase() === 'female' ? '#d81b60' : undefined }}>
                          {u.gender.charAt(0).toUpperCase() + u.gender.slice(1).toLowerCase()}
                        </span>
                      )}
                    </Typography>
                    <Button size="small" variant="contained" onClick={() => handleAddParticipant(u)}>
                      Add
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
